/**
 * Type overrides — size, weight, italic, underline — as combos on each text base.
 *
 * The named scales (`lp-h2`, `lp-body`…) carry Collctiv's real measurements and their
 * responsive behaviour, and they remain the starting point. But "the preset list is too
 * coarse" was a fair complaint: a marketer who wants 32px body copy had no way to ask for it
 * short of picking a heading class and breaking the page outline.
 *
 * So the size choice is a ladder rather than a free number. Every step is a real class in the
 * registry, which is what keeps the export guarantee intact — `emitXscp` still throws on a
 * class it has never heard of, and there is still no path from the UI to an arbitrary CSS
 * value. In practice a 14-step ladder from 12px to 88px is "custom size" for a marketing page,
 * and unlike a free number it cannot produce 37px text or a line-height nobody chose.
 *
 * The matrix is generated rather than written out: 11 bases × 20 overrides. Only the classes a
 * page actually uses reach the Webflow payload (see `StyleRegistry`), so the unused ones cost
 * nothing but a few KB in the editor's own stylesheet.
 */

import type { Longhand } from '../longhand.js';
import { colour, fontStack, space } from '../tokens.js';
import { BOX_RESET, TEXT_RESET, type StyleDef } from '../styleDef.js';

/** The bases that expose type overrides: everything Heading and Text can select. */
export const OVERRIDABLE_TEXT_BASES = [
  'lp-display',
  'lp-h1',
  'lp-h2',
  'lp-h2-panel',
  'lp-h3',
  'lp-h4',
  'lp-lead',
  'lp-body',
  'lp-body-sm',
  'lp-small',
  'lp-eyebrow',
] as const;

/** `lp-body` → `body`, matching the suffix convention the align/on-dark combos already use. */
const suffix = (base: string): string => base.replace('lp-', '');

const typeCombo = (name: string, parent: string, base: Longhand): StyleDef => ({
  name,
  comb: true as const,
  parent,
  base,
});

/* ---------------------------------------------------------------- *
 * Size
 * ---------------------------------------------------------------- */

export const TYPE_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64, 72, 88] as const;

/**
 * Line-height comes with the size, because it is the half people forget.
 *
 * Tight ratios on display type and looser ones on body copy — setting 64px text at 1.4 leaves
 * a heading with canyons between its lines, and setting 16px at 1.15 makes a paragraph
 * unreadable. Nobody should have to think about this, so the ladder decides.
 */
function lineHeightFor(size: number): number {
  if (size <= 18) return Math.round(size * 1.5);
  if (size <= 24) return Math.round(size * 1.4);
  if (size <= 40) return Math.round(size * 1.3);
  return Math.round(size * 1.2);
}

/**
 * Big type has to come down on a phone or it overflows.
 *
 * A 72px heading in a 375px viewport is roughly five characters per line. The same ~70% step
 * the named scales already take, applied automatically so an overridden size is no more
 * dangerous than a preset one.
 */
function sizeVariants(size: number): StyleDef['variants'] | undefined {
  if (size < 40) return undefined;
  const small = Math.round(size * 0.68);
  return {
    small: { 'font-size': `${small}px`, 'line-height': `${lineHeightFor(small)}px` },
  };
}

export const typeSizeCombos: StyleDef[] = OVERRIDABLE_TEXT_BASES.flatMap((base) =>
  TYPE_SIZES.map((size) => ({
    ...typeCombo(`is-size-${size}-${suffix(base)}`, base, {
      'font-size': `${size}px`,
      'line-height': `${lineHeightFor(size)}px`,
    }),
    ...(sizeVariants(size) ? { variants: sizeVariants(size) } : {}),
  })),
);

/* ---------------------------------------------------------------- *
 * Weight, italic, underline
 * ---------------------------------------------------------------- */

export const TYPE_WEIGHTS = [
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semibold' },
  { value: '700', label: 'Bold' },
] as const;

export const typeWeightCombos: StyleDef[] = OVERRIDABLE_TEXT_BASES.flatMap((base) =>
  TYPE_WEIGHTS.map((w) =>
    typeCombo(`is-weight-${w.value}-${suffix(base)}`, base, { 'font-weight': w.value }),
  ),
);

export const typeItalicCombos: StyleDef[] = OVERRIDABLE_TEXT_BASES.map((base) =>
  typeCombo(`is-italic-${suffix(base)}`, base, { 'font-style': 'italic' }),
);

export const typeUnderlineCombos: StyleDef[] = OVERRIDABLE_TEXT_BASES.map((base) =>
  typeCombo(`is-underline-${suffix(base)}`, base, {
    'text-decoration-line': 'underline',
    // Without this the underline sits on the glyphs at large sizes.
    'text-underline-offset': '3px',
  }),
);

/* ---------------------------------------------------------------- *
 * Space below
 * ---------------------------------------------------------------- */

/**
 * Extra space under one heading or paragraph.
 *
 * Layout here is gap-driven and text carries no margins, which is deliberate: a margin on the
 * element *adds* to the container's gap rather than replacing it, so the two compound and
 * spacing becomes impossible to reason about. That rule gave consistent pages and no way to
 * tighten a heading against the paragraph under it — which is the control being asked for.
 *
 * So this is additive, and the field says so. To *reduce* the space between two elements you
 * lower the container's gap; to single out one pair, you add here. That is the honest model,
 * and it is why the label is "Extra space below" rather than "Spacing".
 */
export const SPACE_BELOW_STEPS = [0, 1, 2, 3, 4, 5, 6, 7, 8] as const;

export const typeSpaceBelowCombos: StyleDef[] = OVERRIDABLE_TEXT_BASES.flatMap((base) =>
  SPACE_BELOW_STEPS.filter((s) => s > 0).map((step) =>
    typeCombo(`is-mb-${step}-${suffix(base)}`, base, { 'margin-bottom': space[step] }),
  ),
);

/* ---------------------------------------------------------------- *
 * Inline emphasis
 * ---------------------------------------------------------------- */

/**
 * Bold and italic for *part* of a sentence.
 *
 * These inherit their metrics the way `lp-link` does, rather than declaring a size of their
 * own: a `<strong>` inside a 64px heading and one inside 14px fine print must both take the
 * size of the text around them. What they do declare is `font-family` and `font-weight`,
 * because Collctiv's 391KB stylesheet styles bare `strong` and `em` and those rules inherit
 * straight into a pasted section.
 */
export const lpStrong: StyleDef = {
  name: 'lp-strong',
  base: {
    ...BOX_RESET,
    ...TEXT_RESET,
    'font-family': 'inherit',
    'font-size': 'inherit',
    'line-height': 'inherit',
    'font-weight': '700',
    color: 'inherit',
  },
};

export const lpEm: StyleDef = {
  name: 'lp-em',
  base: {
    ...BOX_RESET,
    ...TEXT_RESET,
    'font-family': 'inherit',
    'font-size': 'inherit',
    'line-height': 'inherit',
    'font-weight': 'inherit',
    'font-style': 'italic',
    color: 'inherit',
  },
};

/** Plain text that needs the brand font but no scale of its own. */
export const lpInline: StyleDef = {
  name: 'lp-inline',
  base: {
    ...BOX_RESET,
    ...TEXT_RESET,
    'font-family': fontStack,
    'font-size': 'inherit',
    'line-height': 'inherit',
    'font-weight': 'inherit',
    color: colour.ink,
  },
};

export const typeOverrideStyles: StyleDef[] = [
  lpStrong,
  lpEm,
  lpInline,
  ...typeSizeCombos,
  ...typeSpaceBelowCombos,
  ...typeWeightCombos,
  ...typeItalicCombos,
  ...typeUnderlineCombos,
];
